import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';
import { BELOW_MD, BELOW_SM } from '@/lib/breakpoints';

// Sidebar collapse state. Three inputs combine into one boolean:
//
//   1. `manual` - the user's last toggle. null = no choice, follow auto.
//      Persisted to localStorage so the next visit remembers.
//   2. `auto`   - viewport-driven, true when viewport < --bp-md. Reset
//      on every matchMedia change; never persisted.
//   3. `routeForced` - routes that need the rail collapsed regardless
//      of user/auto (Studio). The view sets this on mount and clears it
//      on unmount; the manual preference survives the visit.
//
// Effective state, in precedence:
//   isMobile ? expanded(drawer) : routeForced || (manual ?? auto)
//
// Below --bp-sm the nav is an off-canvas drawer (AppShell), not an inline
// column, so the rail concept doesn't apply: the drawer always shows full
// labels. `collapsed` therefore short-circuits to false on phones, and a
// separate `mobileOpen` flag drives whether the drawer is slid in.
//
// Toggling sets `manual` to the inverse of current effective, which
// overrides auto for the rest of the session. Clear via `useAuto()`
// if we ever surface a "reset" affordance.

const STORAGE_KEY = 'hq.ui.sidebar.manual';

function readManual(): boolean | null {
  if (typeof localStorage === 'undefined') return null;
  const raw = localStorage.getItem(STORAGE_KEY);
  if (raw === 'collapsed') return true;
  if (raw === 'expanded') return false;
  return null;
}

function writeManual(value: boolean | null) {
  if (typeof localStorage === 'undefined') return;
  if (value === null) localStorage.removeItem(STORAGE_KEY);
  else localStorage.setItem(STORAGE_KEY, value ? 'collapsed' : 'expanded');
}

export const useSidebarStore = defineStore('sidebar', () => {
  const manual = ref<boolean | null>(readManual());
  const auto = ref<boolean>(
    typeof window !== 'undefined' ? window.matchMedia(BELOW_MD).matches : false,
  );
  // Phone-width: the nav is a drawer, not an inline rail. Drives `collapsed`
  // (always expanded in the drawer) and gates the `mobileOpen` overlay.
  const isMobile = ref<boolean>(
    typeof window !== 'undefined' ? window.matchMedia(BELOW_SM).matches : false,
  );
  // Whether the off-canvas drawer is slid in (phones only). Starts closed and
  // never persists - a fresh load / route always lands with content visible.
  const mobileOpen = ref<boolean>(false);
  const routeForced = ref<boolean>(false);
  // The user expanded the rail despite a route force (Studio). Lets the expand
  // toggle work in the forced state instead of leaving the user stuck in the
  // rail. Reset on every route-force change so a fresh forced visit re-collapses.
  const forceReleased = ref<boolean>(false);

  // Watch viewport. addEventListener('change', ...) on the MediaQueryList
  // is the standard pattern; we keep the listeners for the app lifetime
  // (no teardown needed - Pinia stores live until reload).
  if (typeof window !== 'undefined') {
    window.matchMedia(BELOW_MD).addEventListener('change', (e) => { auto.value = e.matches; });
    window.matchMedia(BELOW_SM).addEventListener('change', (e) => {
      isMobile.value = e.matches;
      // Growing back to a desktop width must not leave a stale drawer flag that
      // would re-open mid-air on the next phone resize.
      if (!e.matches) mobileOpen.value = false;
    });
  }

  const collapsed = computed(() => {
    // On a phone the nav is a drawer with full labels - never the rail.
    if (isMobile.value) return false;
    return (routeForced.value && !forceReleased.value)
      ? true
      : (manual.value !== null ? manual.value : auto.value);
  });

  watch(manual, (v) => writeManual(v));

  function toggle() {
    // Under a route-forced rail (Studio), the first toggle RELEASES the force
    // so the user can expand - identical affordance to a manual collapse.
    if (routeForced.value && !forceReleased.value) {
      forceReleased.value = true;
      return;
    }
    manual.value = !collapsed.value;
  }
  function collapse() { manual.value = true; }
  function expand() { manual.value = false; }
  function useAuto() { manual.value = null; }
  function setRouteForce(force: boolean) {
    routeForced.value = force;
    forceReleased.value = false;  // a fresh forced visit starts collapsed
  }

  // Off-canvas drawer (phones). openMobile/closeMobile/toggleMobile drive the
  // slide-in; AppShell closes it on navigation, scrim tap, and Escape.
  function openMobile() { mobileOpen.value = true; }
  function closeMobile() { mobileOpen.value = false; }
  function toggleMobile() { mobileOpen.value = !mobileOpen.value; }

  return {
    manual, auto, isMobile, mobileOpen, routeForced, forceReleased, collapsed,
    toggle, collapse, expand, useAuto, setRouteForce,
    openMobile, closeMobile, toggleMobile,
  };
});
